import { Injectable } from "@angular/core";
import { SopoPopupService } from "./sopo-popup/service/sopo-popup.service";
import { SopoDialogRef, SopoDialogState } from "./sopo-popup/interfaces/dialog";
import { firstValueFrom } from "rxjs";
import { DebugModeService } from "./core/firebase/debug-mode.service";

const CHEAT_CODE_TIMEOUT_SECONDS = 10000;

const CHEAT_CODE_STORAGE_KEY = {
    GEOFENCE_OVERRIDE_ENABLED: 'geofenceOverrideEnabled',
    OVERRIDE_JACKPOT_MULTIPLIER: 'overrideJpMultiplier',
};

const CHEAT_CODE_INPUT = {
    ENABLE_GEOFENCE_OVERRIDE: 'enable-geofence-override',
    SET_JACKPOT_MULTIPLIER: 'set-jackpot-multiplier',
};

type JackpotMultiplierIndex = -1 | 0 | 1 | 2 | 3 | 4 | 5 | 6;

@Injectable({
	providedIn: 'root'
})
export class SopoCheatService {
	private cheatClickCounting = false;
    private cheatClickCount = 0;
	private cheatResetTimeout: NodeJS.Timeout;

    private _geofencingOverrideEnabled = false;
    private _jackpotMultiplierOverride: JackpotMultiplierIndex = -1;
    private _cheatCodePromptDialogRef: SopoDialogRef;

    get isGeofencingOverrideEnabled(): boolean {
        return this._geofencingOverrideEnabled && this.debugModeService.isDebugUser;
    }

    async checkGeofencingOverride(): Promise<boolean> {
        if (!this._geofencingOverrideEnabled) return false;
        return this.debugModeService.checkDebugStatus();
    }

    get jackpotMultiplier(): JackpotMultiplierIndex {
        return this.debugModeService.isDebugUser ? this._jackpotMultiplierOverride : -1;
    }

	constructor(
		private sopoPopupService: SopoPopupService,
		private debugModeService: DebugModeService,
	) {
        this.initialize();
    }

	async countForCheat() {
        if (!this.cheatClickCounting) {
            this.startCheatClickTimer();
        }

        this.cheatClickCount++;
        if (this.cheatClickCount === 10) {
            if (!await this.debugModeService.checkDebugStatus()) return;
            await this.promptForCheatCode();
        }
    }

    reset(): void {
        this._geofencingOverrideEnabled = false;
        localStorage.removeItem(CHEAT_CODE_STORAGE_KEY.GEOFENCE_OVERRIDE_ENABLED);
    }

    private initialize() {
        const geofencingOverrideEnabled = localStorage.getItem(CHEAT_CODE_STORAGE_KEY.GEOFENCE_OVERRIDE_ENABLED);
        if (geofencingOverrideEnabled === 'true') {
            this._geofencingOverrideEnabled = true;
        }
        const jackpotMultiplierOverride = localStorage.getItem(CHEAT_CODE_STORAGE_KEY.OVERRIDE_JACKPOT_MULTIPLIER);
        if (jackpotMultiplierOverride && jackpotMultiplierOverride !== 'off') {
            this._jackpotMultiplierOverride = Number(jackpotMultiplierOverride) as JackpotMultiplierIndex;
        }
    }

    private showAlert(header: string, message: string) {
        this.sopoPopupService.showAlert('app', {
            header: header,
            message: message,
            dismissAfter: 4000,
        });
    }

	private startCheatClickTimer(): void {
        this.cheatClickCounting = true;
        this.cheatClickCount = 0;
        this.cheatResetTimeout = setTimeout(() => {
            this.cheatClickCount = 0;
            this.cheatClickCounting = false;
        }, CHEAT_CODE_TIMEOUT_SECONDS);
    }

	private async promptForCheatCode(): Promise<void> {
        this.cheatClickCount = 0;
        if (this._cheatCodePromptDialogRef && this._cheatCodePromptDialogRef.getState() !== SopoDialogState.CLOSED) return;

        this._cheatCodePromptDialogRef = this.sopoPopupService.getPromptForStringDialogRef('app', {
            closeButton: true,
            data: {
                header: 'Debug menu',
                body: `Enter Debug Code`,
                placeholder: 'Code',
                okText: 'Activate',
                noPattern: true,
            },
            backdropDismiss: false,
        });

        const answer = this._cheatCodePromptDialogRef ? await firstValueFrom(this._cheatCodePromptDialogRef.afterClosed) : null;

        if (!answer || !answer.startsWith('>')) {
            this.showAlert('Error', 'Commands must start with \'>\'');
            return;
        }

        const splittedCommand = answer.substring(1).trim().split('=');
        const command = splittedCommand[0];
        const commandValue = splittedCommand[1];

        switch (command) {
            case CHEAT_CODE_INPUT.ENABLE_GEOFENCE_OVERRIDE:
                this._geofencingOverrideEnabled = !this._geofencingOverrideEnabled;
                if (this._geofencingOverrideEnabled) {
                    localStorage.setItem(CHEAT_CODE_STORAGE_KEY.GEOFENCE_OVERRIDE_ENABLED, 'true');
                    this.showAlert('Success', 'Geofence override enabled');
                } else {
                    localStorage.removeItem(CHEAT_CODE_STORAGE_KEY.GEOFENCE_OVERRIDE_ENABLED);
                    this.showAlert('Success', 'Geofence override disabled');
                }
                break;
            case CHEAT_CODE_INPUT.SET_JACKPOT_MULTIPLIER:
                if (!commandValue || !['off', '0', '1', '2', '3', '4', '5', '6'].includes(commandValue)) {
                    this.showAlert('Fail', 'Incorrect debug code');
                    return;
                }
                const numberValue = Number(commandValue);
                if (!isNaN(numberValue)) {
                    this._jackpotMultiplierOverride = numberValue as JackpotMultiplierIndex;
                    localStorage.setItem(CHEAT_CODE_STORAGE_KEY.OVERRIDE_JACKPOT_MULTIPLIER, commandValue);
                } else {
                    this._jackpotMultiplierOverride = -1;
                    localStorage.removeItem(CHEAT_CODE_STORAGE_KEY.OVERRIDE_JACKPOT_MULTIPLIER);
                }
                this.showAlert('Success', 'Cheat code activation was successful');
                break;
            default:
                this.showAlert('Fail', 'Incorrect debug code');
        }
    }
}
